/**
 * Firm Agents - Manage firm-level AI automation agents
 * - Cross-family document chaser
 * - Portfolio alerts
 * - Client reporting
 * - Onboarding workflows
 */

import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useToast } from "@/hooks/use-toast";
import {
  Bot,
  Mail,
  Bell,
  FileText,
  UserPlus,
  Play,
  Pause,
  Settings,
  History,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Building2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface FirmAgent {
  id: string;
  name: string;
  agent_type: 'cross_family_chaser' | 'firm_portfolio_alert' | 'client_reporting' | 'onboarding_workflow';
  status: 'active' | 'paused' | 'error';
  config: Record<string, unknown>;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
}

interface AgentRun {
  id: string;
  agent_id: string;
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'completed' | 'failed';
  results: Record<string, unknown> | null;
  error: string | null;
}

const AGENT_TYPES = {
  cross_family_chaser: {
    name: 'Cross-Family Document Chaser',
    description: 'Consolidates document chase requests across families for each GP',
    icon: Mail,
    color: 'bg-blue-500',
  },
  firm_portfolio_alert: {
    name: 'Portfolio Alert Agent',
    description: 'Monitors thresholds and triggers alerts across all families',
    icon: Bell,
    color: 'bg-amber-500',
  },
  client_reporting: {
    name: 'Client Reporting Agent',
    description: 'Auto-generates periodic reports for each family',
    icon: FileText,
    color: 'bg-green-500',
  },
  onboarding_workflow: {
    name: 'Onboarding Workflow Agent',
    description: 'Automates new family setup and welcome sequences',
    icon: UserPlus,
    color: 'bg-purple-500',
  },
};

export default function FirmAgents() {
  const queryClient = useQueryClient();
  const { orgId, isMFO, isLoading: tenantLoading } = useTenant();
  const { toast } = useToast();
  const [selectedAgent, setSelectedAgent] = useState<FirmAgent | null>(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configAgent, setConfigAgent] = useState<FirmAgent | null>(null);
  const [activeTab, setActiveTab] = useState("agents");

  // Fetch agents - MUST be before any conditional returns (React hooks rules)
  const { data: agents, isLoading: agentsLoading } = useQuery({
    queryKey: ['firm-agents', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('firm_agents')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as FirmAgent[];
    },
    enabled: !!orgId && isMFO,
  });

  // Fetch recent agent runs
  const { data: recentRuns, isLoading: runsLoading } = useQuery({
    queryKey: ['firm-agent-runs', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      // Get agent IDs first
      const { data: agentData } = await supabase
        .from('firm_agents')
        .select('id')
        .eq('org_id', orgId);

      if (!agentData || agentData.length === 0) return [];

      const agentIds = agentData.map(a => a.id);

      const { data, error } = await supabase
        .from('firm_agent_runs')
        .select('*')
        .in('agent_id', agentIds)
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as AgentRun[];
    },
    enabled: !!orgId && isMFO,
  });

  // Toggle agent status mutation
  const toggleAgentMutation = useMutation({
    mutationFn: async ({ agentId, newStatus }: { agentId: string; newStatus: 'active' | 'paused' }) => {
      const { error } = await supabase
        .from('firm_agents')
        .update({ status: newStatus })
        .eq('id', agentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-agents'] });
      toast({ title: "Agent status updated" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Run agent manually mutation
  const runAgentMutation = useMutation({
    mutationFn: async (agentId: string) => {
      // This would trigger the edge function
      // For now, we'll just create a run record
      const { data: { session } } = await supabase.auth.getSession();

      // In production, this would call an edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-firm-agents`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ agentId })
        }
      ).catch(() => null);

      if (!response || !response.ok) {
        // Fallback: just update last_run_at for demo
        await supabase
          .from('firm_agents')
          .update({ last_run_at: new Date().toISOString() })
          .eq('id', agentId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-agents'] });
      queryClient.invalidateQueries({ queryKey: ['firm-agent-runs'] });
      toast({ title: "Agent run triggered" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Create new agent mutation
  const createAgentMutation = useMutation({
    mutationFn: async (agentType: keyof typeof AGENT_TYPES) => {
      if (!orgId) throw new Error('No org selected');

      const typeInfo = AGENT_TYPES[agentType];
      const { error } = await supabase
        .from('firm_agents')
        .insert({
          org_id: orgId,
          name: typeInfo.name,
          agent_type: agentType,
          status: 'paused',
          config: getDefaultConfig(agentType),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-agents'] });
      toast({ title: "Agent created" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Update agent config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({ agentId, config }: { agentId: string; config: Record<string, unknown> }) => {
      const { error } = await supabase
        .from('firm_agents')
        .update({ config })
        .eq('id', agentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-agents'] });
      toast({ title: "Agent configuration updated" });
      setShowConfigDialog(false);
      setConfigAgent(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function getDefaultConfig(agentType: keyof typeof AGENT_TYPES): Record<string, unknown> {
    switch (agentType) {
      case 'cross_family_chaser':
        return {
          chase_interval_days: 7,
          max_chase_attempts: 3,
          email_template: 'default',
          excluded_gps: [],
        };
      case 'firm_portfolio_alert':
        return {
          concentration_threshold_pct: 20,
          drawdown_threshold_pct: 10,
          stale_nav_days: 90,
          notify_email: true,
          notify_slack: false,
        };
      case 'client_reporting':
        return {
          frequency: 'quarterly',
          report_template: 'standard',
          include_performance: true,
          include_holdings: true,
          auto_send: false,
        };
      case 'onboarding_workflow':
        return {
          create_default_entities: true,
          setup_document_requirements: true,
          send_welcome_email: true,
          schedule_intro_meeting: true,
        };
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'paused':
        return <Badge variant="secondary">Paused</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'running':
        return <Badge className="bg-blue-500">Running</Badge>;
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get agents by type for the setup section
  const existingAgentTypes = new Set(agents?.map(a => a.agent_type) || []);

  const isLoading = tenantLoading || agentsLoading;

  // Redirect SFO users (AFTER all hooks)
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bot className="h-6 w-6" />
            Firm Agents
          </h1>
          <p className="text-muted-foreground">
            Manage AI-powered automation across all families
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            {agents?.filter(a => a.status === 'active').length || 0} Active
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="history">Run History</TabsTrigger>
          <TabsTrigger value="setup">Setup</TabsTrigger>
        </TabsList>

        {/* Agents Tab */}
        <TabsContent value="agents" className="space-y-4">
          {agents && agents.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {agents.map((agent) => {
                const typeInfo = AGENT_TYPES[agent.agent_type];
                const Icon = typeInfo?.icon || Bot;

                return (
                  <Card key={agent.id} className="hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${typeInfo?.color || 'bg-muted'} text-white`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{agent.name}</CardTitle>
                            <CardDescription className="text-xs">
                              {typeInfo?.description}
                            </CardDescription>
                          </div>
                        </div>
                        {getStatusBadge(agent.status)}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Last run</span>
                        <span>
                          {agent.last_run_at
                            ? formatDistanceToNow(new Date(agent.last_run_at), { addSuffix: true })
                            : 'Never'}
                        </span>
                      </div>
                      {agent.next_run_at && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Next run</span>
                          <span>
                            {formatDistanceToNow(new Date(agent.next_run_at), { addSuffix: true })}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={agent.status === 'active'}
                            onCheckedChange={(checked) =>
                              toggleAgentMutation.mutate({
                                agentId: agent.id,
                                newStatus: checked ? 'active' : 'paused',
                              })
                            }
                            disabled={toggleAgentMutation.isPending}
                          />
                          <span className="text-sm">
                            {agent.status === 'active' ? 'Active' : 'Paused'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setConfigAgent(agent);
                              setShowConfigDialog(true);
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedAgent(agent)}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => runAgentMutation.mutate(agent.id)}
                            disabled={runAgentMutation.isPending}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Run
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bot className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No agents configured</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Set up firm-level agents to automate document chasing, alerts, and reporting.
                </p>
                <Button onClick={() => setActiveTab("setup")}>
                  Set Up Agents
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Agent Run History</CardTitle>
              <CardDescription>Recent agent executions and results</CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : recentRuns && recentRuns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Results</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentRuns.map((run) => {
                      const agent = agents?.find(a => a.id === run.agent_id);
                      const duration = run.completed_at
                        ? Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 1000)
                        : null;

                      return (
                        <TableRow key={run.id}>
                          <TableCell className="font-medium">
                            {agent?.name || 'Unknown Agent'}
                          </TableCell>
                          <TableCell>
                            {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}
                          </TableCell>
                          <TableCell>
                            {duration !== null ? `${duration}s` : '-'}
                          </TableCell>
                          <TableCell>{getStatusBadge(run.status)}</TableCell>
                          <TableCell>
                            {run.status === 'failed' && run.error ? (
                              <span className="text-destructive text-sm">{run.error}</span>
                            ) : run.results ? (
                              <span className="text-sm">
                                {Object.keys(run.results).length} items processed
                              </span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No agent runs yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Setup Tab */}
        <TabsContent value="setup" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(AGENT_TYPES).map(([type, info]) => {
              const Icon = info.icon;
              const exists = existingAgentTypes.has(type as keyof typeof AGENT_TYPES);

              return (
                <Card key={type} className={exists ? 'opacity-60' : ''}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${info.color} text-white`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-base">{info.name}</CardTitle>
                        <CardDescription>{info.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full"
                      variant={exists ? "secondary" : "default"}
                      disabled={exists || createAgentMutation.isPending}
                      onClick={() => createAgentMutation.mutate(type as keyof typeof AGENT_TYPES)}
                    >
                      {exists ? (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Already Set Up
                        </>
                      ) : createAgentMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Bot className="h-4 w-4" />
                          Set Up Agent
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Agent History Sheet */}
      <Sheet open={!!selectedAgent} onOpenChange={(open) => !open && setSelectedAgent(null)}>
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {selectedAgent?.name} - History
            </SheetTitle>
            <SheetDescription>
              Recent runs and results for this agent
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {recentRuns
              ?.filter(r => r.agent_id === selectedAgent?.id)
              .slice(0, 10)
              .map((run) => {
                const duration = run.completed_at
                  ? Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 1000)
                  : null;

                return (
                  <Card key={run.id}>
                    <CardHeader className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {run.status === 'completed' ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : run.status === 'failed' ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <Clock className="h-4 w-4 text-blue-500" />
                          )}
                          <span className="text-sm font-medium">
                            {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}
                          </span>
                        </div>
                        {getStatusBadge(run.status)}
                      </div>
                    </CardHeader>
                    <CardContent className="py-3 pt-0">
                      {duration !== null && (
                        <div className="text-sm text-muted-foreground mb-2">
                          Duration: {duration}s
                        </div>
                      )}
                      {run.error && (
                        <div className="text-sm text-destructive">
                          Error: {run.error}
                        </div>
                      )}
                      {run.results && (
                        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                          {JSON.stringify(run.results, null, 2)}
                        </pre>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

            {(!recentRuns || recentRuns.filter(r => r.agent_id === selectedAgent?.id).length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                No runs for this agent yet
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure {configAgent?.name}</DialogTitle>
            <DialogDescription>
              Adjust settings for this agent
            </DialogDescription>
          </DialogHeader>

          {configAgent && (
            <AgentConfigForm
              agent={configAgent}
              onSave={(config) => updateConfigMutation.mutate({ agentId: configAgent.id, config })}
              onCancel={() => {
                setShowConfigDialog(false);
                setConfigAgent(null);
              }}
              isSaving={updateConfigMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Agent config form component
function AgentConfigForm({
  agent,
  onSave,
  onCancel,
  isSaving,
}: {
  agent: FirmAgent;
  onSave: (config: Record<string, unknown>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [config, setConfig] = useState(agent.config || {});

  const updateConfig = (key: string, value: unknown) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const parseIntSafe = (value: string, fallback: number): number => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  const renderConfigFields = () => {
    switch (agent.agent_type) {
      case 'cross_family_chaser':
        return (
          <>
            <div className="space-y-2">
              <Label>Chase Interval (days)</Label>
              <Input
                type="number"
                value={config.chase_interval_days || 7}
                onChange={(e) => updateConfig('chase_interval_days', parseIntSafe(e.target.value, 7))}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Chase Attempts</Label>
              <Input
                type="number"
                value={config.max_chase_attempts || 3}
                onChange={(e) => updateConfig('max_chase_attempts', parseIntSafe(e.target.value, 3))}
              />
            </div>
          </>
        );

      case 'firm_portfolio_alert':
        return (
          <>
            <div className="space-y-2">
              <Label>Concentration Threshold (%)</Label>
              <Input
                type="number"
                value={config.concentration_threshold_pct || 20}
                onChange={(e) => updateConfig('concentration_threshold_pct', parseIntSafe(e.target.value, 20))}
              />
            </div>
            <div className="space-y-2">
              <Label>Drawdown Threshold (%)</Label>
              <Input
                type="number"
                value={config.drawdown_threshold_pct || 10}
                onChange={(e) => updateConfig('drawdown_threshold_pct', parseIntSafe(e.target.value, 10))}
              />
            </div>
            <div className="space-y-2">
              <Label>Stale NAV Alert (days)</Label>
              <Input
                type="number"
                value={config.stale_nav_days || 90}
                onChange={(e) => updateConfig('stale_nav_days', parseIntSafe(e.target.value, 90))}
              />
            </div>
          </>
        );

      case 'client_reporting':
        return (
          <>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2"
                value={config.frequency || 'quarterly'}
                onChange={(e) => updateConfig('frequency', e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.include_performance !== false}
                onCheckedChange={(checked) => updateConfig('include_performance', checked)}
              />
              <Label>Include Performance Metrics</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.auto_send === true}
                onCheckedChange={(checked) => updateConfig('auto_send', checked)}
              />
              <Label>Auto-send to Clients</Label>
            </div>
          </>
        );

      case 'onboarding_workflow':
        return (
          <>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.create_default_entities !== false}
                onCheckedChange={(checked) => updateConfig('create_default_entities', checked)}
              />
              <Label>Create Default Entities</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.setup_document_requirements !== false}
                onCheckedChange={(checked) => updateConfig('setup_document_requirements', checked)}
              />
              <Label>Setup Document Requirements</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.send_welcome_email !== false}
                onCheckedChange={(checked) => updateConfig('send_welcome_email', checked)}
              />
              <Label>Send Welcome Email</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.schedule_intro_meeting !== false}
                onCheckedChange={(checked) => updateConfig('schedule_intro_meeting', checked)}
              />
              <Label>Schedule Intro Meeting</Label>
            </div>
          </>
        );

      default:
        return (
          <Textarea
            value={JSON.stringify(config, null, 2)}
            onChange={(e) => {
              try {
                setConfig(JSON.parse(e.target.value));
              } catch {
                // Invalid JSON, ignore
              }
            }}
            className="font-mono text-sm"
            rows={10}
          />
        );
    }
  };

  return (
    <div className="space-y-4 py-4">
      {renderConfigFields()}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(config)} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}
